import React, { useEffect, useState } from 'react';
import { CSSTransition } from 'react-transition-group';
import { Formik, ErrorMessage } from 'formik';
import uniqueId from 'lodash/uniqueId';
import './styles.css';
import BasicButton from '../../../../components/BasicButton';
import BasicButtonGroup from '../../../../components/BasicButtonGroup';
import BasicCheckbox from '../../../../components/BasicCheckbox';
import BasicInput from '../../../../components/BasicInput';
import BasicLabel from '../../../../components/BasicLabel';
import BasicSelect from '../../../../components/BasicSelect';
import BasicSubmitButton from '../../../../components/BasicSubmitButton';
import BasicTextArea from '../../../../components/BasicTextArea';
import HorizontalLoader from '../../../../components/HorizontalLoader';
import LazyLoadSelect from '../../../../components/LazyLoadSelect';
import LoadMask from '../../../../components/LoadMask';
import {ANIMATION_TIMEOUT_APPEAR} from '../../../../constants/animation';
import ENDPOINT from '../../../../constants/endpoints';
import useApi from '../../../../hooks/useApi';
import {ProfileSendReviewFormValidationSchema} from './validate';

const ProfileSendReviewForm = props => {
  const {
    params: { id },
    actions: { handleError, proceed, dismiss },
  } = props;

  const api = useApi();

  const [state, setState] = useState({
    init: true,
    cacheUniq: uniqueId('ср_'),
    showAgreeCheckbox: false,
    fields: {
      type: null,
      emId: null,
      subject: '',
      comments: '',
      isCheck: true,
      agree: false,
      id,
    },
    options: { type: [], position: [] },
  });

  useEffect(() => {
    api.get(
      ENDPOINT.FORM.GET.CANDIDATE_NOTIFY_REVIEW_FORM,
      { id },
      {
        handleError: response => handleError(response),
        handleSuccess: response =>
          setState(state => ({
            ...state,
            init: true,
            ...response,
          })),
      }
    );
  }, []);

  const handleSubmit = (
    values,
    { setSubmitting, setErrors, setFieldValue }
  ) => {
    const params = { ...values };
    Object.keys(params).forEach(k => {
      if (!params[k] || k === 'agree') {
        delete params[k];
      } else if (typeof params[k] === 'object') {
        params[k] = params[k].value;
      }
    });

    api.post(ENDPOINT.FORM.SAVE.CANDIDATE_NOTIFY_REVIEW_FORM, params, {
      handleError: response => {
        setSubmitting(false);
        handleError(response, setErrors);
      },
      handleSuccess: response => {
        setSubmitting(false);
        if (response.warning) {
          setFieldValue('isCheck', false, false);
          setState(state => ({ ...state, showAgreeCheckbox: true }));
        } else {
          proceed();
        }
      },
    });
  };

  const getFirmOptions = (query, offset, params) => {
    return new Promise((resolve, reject) => {
      api.get(
        ENDPOINT.FORM.OPTIONS.CANDIDATE_NOTIFY_REVIEW_FIRMS,
        { query, offset, ...params },
        {
          handleError: response => reject(response),
          handleSuccess: data => resolve(data),
        }
      );
    });
  };

  const handleTypeChange = (value, { values, setValues }) => {
    setValues(
      {
        ...values,
        type: value,
        emId: null,
        agree: false,
        isCheck: true,
      },
      false
    );

    setState(state => ({
      ...state,
      cacheUniq: uniqueId('ch_'),
      showAgreeCheckbox: false,
    }));
  };

  const handleEmChange = (value, { values, setValues }) => {
    setValues(
      {
        ...values,
        emId: value,
        agree: false,
        isCheck: true,
      },
      false
    );
    setState(state => ({ ...state, showAgreeCheckbox: false }));
  };

  return state.init ? (
    <CSSTransition
      in={true}
      appear={true}
      timeout={ANIMATION_TIMEOUT_APPEAR}
      classNames="fade"
    >
      <div className="form__inner">
        <Formik
          initialValues={state.fields}
          onSubmit={handleSubmit}
          validationSchema={ProfileSendReviewFormValidationSchema}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            handleSubmit,
            isSubmitting,
            setValues,
            setFieldTouched,
          }) => (
            <form
              onSubmit={handleSubmit}
              className="form__content"
              style={{ maxWidth: '100%' }}
            >
              {isSubmitting && <LoadMask />}

              <div
                className="form__group form__group--compact"
                style={{ marginBottom: '15px' }}
              >
                <BasicLabel
                  name="TypeLabel"
                  required={true}
                  text="Type"
                  styles={{
                    fontWeight: 'bold',
                  }}
                />
                <BasicSelect
                  name="type"
                  value={values.type}
                  isValid={(!errors.type && !!touched.type) || !touched.type}
                  placeholder="Select From List"
                  options={state.options.type}
                  onChange={(field, value) =>
                    handleTypeChange(value, { values, setValues })
                  }
                  onBlur={setFieldTouched}
                />

                <ErrorMessage
                  name="type"
                  render={message => (
                    <div className="form__error">{message}</div>
                  )}
                />
              </div>

              {values.type && values.type.value === 'employee' ? (
                <div
                  className="form__group form__group--compact"
                  style={{ marginBottom: '15px' }}
                >
                  <BasicLabel
                    name="TypeLabel"
                    required={true}
                    text="Position"
                    styles={{
                      fontWeight: 'bold',
                    }}
                  />
                  <BasicSelect
                    name="emId"
                    value={values.emId}
                    disabled={!values.type}
                    isValid={(!errors.emId && !!touched.emId) || !touched.emId}
                    placeholder="Select From List"
                    options={state.options.position}
                    onChange={(field, value) =>
                      handleEmChange(value, { values, setValues })
                    }
                    onBlur={setFieldTouched}
                  />

                  <ErrorMessage
                    name="emId"
                    render={message => (
                      <div className="form__error">{message}</div>
                    )}
                  />
                </div>
              ) : (
                <div className="form__group form__group--compact">
                  <LazyLoadSelect
                    cacheUniq={state.cacheUniq}
                    disabled={!values.type}
                    isClearable={true}
                    name="emId"
                    value={values.emId}
                    isValid={(!errors.emId && !!touched.emId) || !touched.emId}
                    label="Firm"
                    labelStyles={{
                      fontWeight: 'bold',
                    }}
                    required={true}
                    placeholder="Start Typing"
                    getOption={(query, offset) =>
                      getFirmOptions(query, offset, {
                        id: values.id,
                        type: values.type.value,
                      })
                    }
                    onChange={(field, value) =>
                      handleEmChange(value, { values, setValues })
                    }
                    onBlur={setFieldTouched}
                  />

                  <ErrorMessage
                    name="emId"
                    render={message => (
                      <div className="form__error">{message}</div>
                    )}
                  />
                </div>
              )}

              <div className="form__group form__group--compact">
                <BasicInput
                  label="Subject"
                  required={true}
                  name="subject"
                  inputType="text"
                  content={values.subject}
                  placeholder="Email subject..."
                  controlFunc={handleChange}
                  isValid={
                    (!errors.subject && !!touched.subject) || !touched.subject
                  }
                  touchFunc={handleBlur}
                  labelStyles={{ fontWeight: 'bold' }}
                />

                <ErrorMessage
                  name="subject"
                  render={message => (
                    <div className="form__error">{message}</div>
                  )}
                />
              </div>

              <div className="form__group form__group--compact">
                <BasicLabel
                  name="comments"
                  required={true}
                  text="Comments"
                  styles={{
                    fontWeight: 'bold',
                  }}
                />
                <BasicTextArea
                  rows={10}
                  name="comments"
                  content={values.comments}
                  resize={false}
                  placeholder="0 characters of the 2500 character limit"
                  controlFunc={handleChange}
                  touchFunc={handleBlur}
                  isValid={
                    (!errors.comments && !!touched.comments) ||
                    !touched.comments
                  }
                />
                <ErrorMessage
                  name="comments"
                  render={message => (
                    <div className="form__error">{message}</div>
                  )}
                />
              </div>

              {state.showAgreeCheckbox && (
                <div className="form__group form__group--compact">
                  <div className="form__row">
                    <BasicCheckbox
                      value={values.agree}
                      singleRow
                      checked={values.agree}
                      title={`You have already sent a request to write ${
                        values.type.label
                      } Review for ${
                        values.emId.label
                      } recently. Please check the checkbox to confirm that you want to send the review request again and click on Send button.`}
                      name="agree"
                      controlFunc={handleChange}
                      labelStyles={{
                        fontFamily: 'Noto Sans, sans-serif',
                        color: '#f40',
                      }}
                    />
                  </div>
                </div>
              )}

              <div className="form__controls form__controls--compact">
                <BasicButtonGroup groupStyles={{ width: '100%' }}>
                  <BasicSubmitButton
                    disabled={state.showAgreeCheckbox && !values.agree}
                    controlFunc={handleSubmit}
                    btnWidth={200}
                    btnHeight={40}
                    btnFontWeight={500}
                    btnArrow={true}
                    btnTextColor="#27282d"
                    btnText="Send Review"
                  />

                  <BasicButton
                    controlFunc={dismiss}
                    btnWidth={200}
                    btnHeight={40}
                    btnFontWeight={500}
                    btnArrow={false}
                    btnTextColor="#27282d"
                    btnText="Cancel"
                  />
                </BasicButtonGroup>
              </div>
            </form>
          )}
        </Formik>
      </div>
    </CSSTransition>
  ) : (
    <div className="form__inner">
      <div className="horizontal-loader__container">
        <HorizontalLoader />
      </div>
    </div>
  );
};

export default ProfileSendReviewForm;
